import { CustomThemeColors } from "models/user-models";

export interface ThemeOption {
  id: string;
  name: string;
  description: string;
  previewColors: CustomThemeColors;
}

export type ThemeMode = "light" | "dark";
export type ThemePalette = Record<string, string>;
export type ThemePreset = Record<ThemeMode, ThemePalette>;

/**
 * Single source of truth for all preset theme definitions in the application.
 * All colors are defined in pure HEX format for full Tailwind color/opacity support.
 */
const LIGHT_THEME_PRESETS: Record<string, ThemePalette> = {
  horizonDefault: {
    "--color-light-primary": "#F4F7FE",
    "--color-blue-secondary": "#4318FF",
    "--color-brand-linear": "#868CFF",
    "--color-brand-50": "#E9E3FF",
    "--color-brand-100": "#C0B8FE",
    "--color-brand-200": "#A195FD",
    "--color-brand-300": "#8171FC",
    "--color-brand-400": "#7551FF",
    "--color-brand-500": "#422AFB",
    "--color-brand-700": "#2111A5",
    "--color-brand-900": "#11047A",
    "--color-navy-400": "#3652ba",
    "--color-navy-500": "#1b3bbb",
    "--color-navy-600": "#24388a",
    "--color-navy-700": "#1B254B",
    "--color-navy-800": "#111c44",
    "--color-navy-900": "#0b1437",
    "--color-copy-btn-surface": "#16A34A",
    "--color-copy-btn-icon": "#ffffff",
    "--color-download-btn-surface": "#6495ED",
    "--color-download-btn-icon": "#ffffff",
    "--color-text-primary": "#1b2559",
    "--color-text-secondary": "#707eae",
    "--color-text-muted": "#a3aed0",
    "--color-surface-primary": "#ffffff",
    "--color-surface-secondary": "#F4F7FE",
    "--color-border-primary": "#e2e8f0",
    "--color-shadow-primary": "#7090b0",
    "--color-shadow-secondary": "#7090b0",
    "--color-scrollbar-thumb": "#a3aed0",
    "--color-scrollbar-track": "#F4F7FE",
  },
  healthSaas: {
    "--color-light-primary": "#e0f7fa",
    "--color-blue-secondary": "#1976d2",
    "--color-brand-linear": "#00acc1",
    "--color-brand-50": "#e0f7fa",
    "--color-brand-100": "#b2ebf2",
    "--color-brand-200": "#80deea",
    "--color-brand-300": "#4dd0e1",
    "--color-brand-400": "#00acc1",
    "--color-brand-500": "#1976d2",
    "--color-brand-700": "#0d47a1",
    "--color-brand-900": "#06285c",
    "--color-navy-400": "#1565c0",
    "--color-navy-500": "#0d47a1",
    "--color-navy-600": "#0a3880",
    "--color-navy-700": "#06285c",
    "--color-navy-800": "#041a3d",
    "--color-navy-900": "#020d1f",
    "--color-copy-btn-surface": "#00acc1",
    "--color-copy-btn-icon": "#ffffff",
    "--color-download-btn-surface": "#1976d2",
    "--color-download-btn-icon": "#ffffff",
    "--color-text-primary": "#0d47a1",
    "--color-text-secondary": "#1976d2",
    "--color-text-muted": "#00acc1",
    "--color-surface-primary": "#ffffff",
    "--color-surface-secondary": "#e0f7fa",
    "--color-border-primary": "#4dd0e1",
    "--color-shadow-primary": "#0d47a1",
    "--color-shadow-secondary": "#0d47a1",
    "--color-scrollbar-thumb": "#4dd0e1",
    "--color-scrollbar-track": "#e0f7fa",
  },
  telehealth: {
    "--color-light-primary": "#e0f2f1",
    "--color-blue-secondary": "#00838f",
    "--color-brand-linear": "#26a69a",
    "--color-brand-50": "#e0f2f1",
    "--color-brand-100": "#b2dfdb",
    "--color-brand-200": "#80cbc4",
    "--color-brand-300": "#4db6ac",
    "--color-brand-400": "#26a69a",
    "--color-brand-500": "#00838f",
    "--color-brand-700": "#004d40",
    "--color-brand-900": "#00241e",
    "--color-navy-400": "#006064",
    "--color-navy-500": "#004d40",
    "--color-navy-600": "#263238",
    "--color-navy-700": "#1e272c",
    "--color-navy-800": "#171f23",
    "--color-navy-900": "#0e1316",
    "--color-copy-btn-surface": "#00838f",
    "--color-copy-btn-icon": "#ffffff",
    "--color-download-btn-surface": "#26a69a",
    "--color-download-btn-icon": "#ffffff",
    "--color-text-primary": "#263238",
    "--color-text-secondary": "#26a69a",
    "--color-text-muted": "#80cbc4",
    "--color-surface-primary": "#ffffff",
    "--color-surface-secondary": "#e0f2f1",
    "--color-border-primary": "#80cbc4",
    "--color-shadow-primary": "#263238",
    "--color-shadow-secondary": "#263238",
    "--color-scrollbar-thumb": "#80cbc4",
    "--color-scrollbar-track": "#e0f2f1",
  },
  refreshersGreen: {
    "--color-light-primary": "#f7faf5",
    "--color-blue-secondary": "#00754a",
    "--color-brand-linear": "#0f8a5f",
    "--color-brand-50": "#e8f3ec",
    "--color-brand-100": "#cce5d5",
    "--color-brand-200": "#a8d1b8",
    "--color-brand-300": "#7fba98",
    "--color-brand-400": "#00754a",
    "--color-brand-500": "#006241",
    "--color-brand-700": "#004d36",
    "--color-brand-900": "#003326",
    "--color-navy-400": "#4f8066",
    "--color-navy-500": "#2f684d",
    "--color-navy-600": "#1f573f",
    "--color-navy-700": "#164a36",
    "--color-navy-800": "#0d3929",
    "--color-navy-900": "#08271d",
    "--color-copy-btn-surface": "#006241",
    "--color-copy-btn-icon": "#ffffff",
    "--color-download-btn-surface": "#00754a",
    "--color-download-btn-icon": "#ffffff",
    "--color-text-primary": "#173b2c",
    "--color-text-secondary": "#557565",
    "--color-text-muted": "#8fa99a",
    "--color-surface-primary": "#ffffff",
    "--color-surface-secondary": "#f1f7f2",
    "--color-border-primary": "#c5d9ca",
    "--color-shadow-primary": "#173b2c",
    "--color-shadow-secondary": "#173b2c",
    "--color-scrollbar-thumb": "#8fa99a",
    "--color-scrollbar-track": "#f7faf5",
  },
  pharmaLab: {
    "--color-light-primary": "#f5f5f5",
    "--color-blue-secondary": "#78909c",
    "--color-brand-linear": "#b0bec5",
    "--color-brand-50": "#ffffff",
    "--color-brand-100": "#f5f5f5",
    "--color-brand-200": "#e0e0e0",
    "--color-brand-300": "#cfd8dc",
    "--color-brand-400": "#b0bec5",
    "--color-brand-500": "#5f6e76",
    "--color-brand-700": "#37474f",
    "--color-brand-900": "#1c272b",
    "--color-navy-400": "#546e7a",
    "--color-navy-500": "#37474f",
    "--color-navy-600": "#263238",
    "--color-navy-700": "#1c272b",
    "--color-navy-800": "#121a1d",
    "--color-navy-900": "#0a0f11",
    "--color-copy-btn-surface": "#78909c",
    "--color-copy-btn-icon": "#ffffff",
    "--color-download-btn-surface": "#b0bec5",
    "--color-download-btn-icon": "#ffffff",
    "--color-text-primary": "#37474f",
    "--color-text-secondary": "#78909c",
    "--color-text-muted": "#b0bec5",
    "--color-surface-primary": "#ffffff",
    "--color-surface-secondary": "#e0e0e0",
    "--color-border-primary": "#b0bec5",
    "--color-shadow-primary": "#37474f",
    "--color-shadow-secondary": "#37474f",
    "--color-scrollbar-thumb": "#b0bec5",
    "--color-scrollbar-track": "#f5f5f5",
  },
  agentCoral: {
    "--color-light-primary": "#f7f4ef",
    "--color-blue-secondary": "#ff5a36",
    "--color-brand-linear": "#ff8b70",
    "--color-brand-50": "#fff0eb",
    "--color-brand-100": "#ffd8ce",
    "--color-brand-200": "#ffc0b3",
    "--color-brand-300": "#ffad97",
    "--color-brand-400": "#ff7048",
    "--color-brand-500": "#ff5a36",
    "--color-brand-700": "#d94326",
    "--color-brand-900": "#8f2417",
    "--color-navy-400": "#77716c",
    "--color-navy-500": "#5b5651",
    "--color-navy-600": "#47433f",
    "--color-navy-700": "#373431",
    "--color-navy-800": "#292725",
    "--color-navy-900": "#1e1c1b",
    "--color-copy-btn-surface": "#373431",
    "--color-copy-btn-icon": "#ffffff",
    "--color-download-btn-surface": "#ff5a36",
    "--color-download-btn-icon": "#ffffff",
    "--color-text-primary": "#2f2c2a",
    "--color-text-secondary": "#706963",
    "--color-text-muted": "#a69e96",
    "--color-surface-primary": "#fffdfb",
    "--color-surface-secondary": "#f7f4ef",
    "--color-border-primary": "#d8d1ca",
    "--color-shadow-primary": "#2f2c2a",
    "--color-shadow-secondary": "#2f2c2a",
    "--color-scrollbar-thumb": "#a69e96",
    "--color-scrollbar-track": "#f7f4ef",
  },

};

const mixHex = (first: string, second: string, amount: number) => {
  const firstValue = first.replace("#", "");
  const secondValue = second.replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(firstValue) || !/^[0-9a-f]{6}$/i.test(secondValue)) return first;
  return `#${[0, 2, 4].map((offset) => {
    const channel = Number.parseInt(firstValue.slice(offset, offset + 2), 16);
    const target = Number.parseInt(secondValue.slice(offset, offset + 2), 16);
    return Math.round(channel + (target - channel) * amount).toString(16).padStart(2, "0");
  }).join("")}`;
};

const colorLuminance = (color: string) => {
  const value = color.replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(value)) return 0;
  const channels = [0, 2, 4].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16) / 255);
  return channels.reduce((total, channel, index) => {
    const linear = channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
    return total + linear * [0.2126, 0.7152, 0.0722][index];
  }, 0);
};

const whiteContrast = (color: string) => 1.05 / (colorLuminance(color) + 0.05);

const darkButtonColor = (color: string) => {
  let adjusted = color;
  let amount = 0;
  while (whiteContrast(adjusted) < 4.5 && amount < 0.7) {
    amount += 0.1;
    adjusted = mixHex(color, "#000000", amount);
  }
  return adjusted;
};

const buildDarkPalette = (lightPalette: ThemePalette): ThemePalette =>
  Object.fromEntries(Object.entries(lightPalette).map(([key, value]) => {
    if (key.includes("surface-primary")) return [key, "#151a18"];
    if (key.includes("surface-secondary") || key.includes("light-primary")) return [key, "#1d2923"];
    if (key.includes("text-primary")) return [key, mixHex(value, "#ffffff", 0.82)];
    if (key.includes("text-secondary")) return [key, mixHex(value, "#ffffff", 0.35)];
    if (key.includes("text-muted")) return [key, mixHex(value, "#ffffff", 0.2)];
    if (key.includes("border")) return [key, mixHex(value, "#000000", 0.45)];
    if (key.includes("shadow")) return [key, "#000000"];
    if (key.includes("scrollbar-track")) return [key, "#1d2923"];
    if (key.includes("scrollbar-thumb")) return [key, "#52635a"];
    if (key.includes("brand-50") || key.includes("brand-100") || key.includes("brand-200") || key.includes("brand-300")) {
      return [key, mixHex(value, "#10251b", 0.5)];
    }
    if (key.includes("brand-400") || key.includes("brand-500") || key.includes("blue-secondary") || key.includes("brand-linear") || key.includes("btn-surface")) {
      return [key, darkButtonColor(value)];
    }
    if (key.includes("brand-700") || key.includes("brand-900")) return [key, mixHex(value, "#ffffff", 0.35)];
    if (key.includes("navy-")) return [key, mixHex(value, "#000000", 0.15)];
    return [key, value];
  }));

export const THEME_PRESETS: Record<string, ThemePreset> = Object.fromEntries(
  Object.entries(LIGHT_THEME_PRESETS).map(([themeId, light]) => [themeId, {
    light,
    dark: buildDarkPalette(light),
  }])
);

/**
 * Human-readable list of preset options for UI selectors and cards.
 */
export const THEME_OPTIONS: ThemeOption[] = [
  {
    id: "horizonDefault",
    name: "Horizon Default",
    description: "The original Horizon UI purple & blue palette. Vibrant, modern, and timeless.",
    previewColors: {
      primary: "#422AFB",
      secondary: "#7551FF",
      bg: "#F4F7FE",
      foreground: "#ffffff",
      textPrimary: "#1b2559",
    },
  },
  {
    id: "healthSaas",
    name: "Health SaaS",
    description: "Efficient, data-driven, and crisp with a software-first feel. Perfect for high-density dashboards.",
    previewColors: {
      primary: "#1976d2",
      secondary: "#00acc1",
      bg: "#e0f7fa",
      foreground: "#ffffff",
      textPrimary: "#0d47a1",
    },
  },
  {
    id: "telehealth",
    name: "Telehealth Teal",
    description: "Balanced, contemporary, and reassuring with digital calm. Ideal for telehealth platforms.",
    previewColors: {
      primary: "#00838f",
      secondary: "#26a69a",
      bg: "#e0f2f1",
      foreground: "#ffffff",
      textPrimary: "#263238",
    },
  },
  {
    id: "pharmaLab",
    name: "Pharma & Lab Neutral",
    description: "Minimal, neutral, and trustworthy with subtle lab sophistication. Great for research & labs.",
    previewColors: {
      primary: "#5f6e76",
      secondary: "#b0bec5",
      bg: "#f5f5f5",
      foreground: "#ffffff",
      textPrimary: "#37474f",
    },
  },
  {
    id: "refreshersGreen",
    name: "Refreshers Green",
    description: "Bright, welcoming, and seasonal with an evergreen base, creamy surfaces, and fresh coral accents.",
    previewColors: {
      primary: "#006241",
      secondary: "#00754a",
      bg: "#f7faf5",
      foreground: "#ffffff",
      textPrimary: "#173b2c",
    },
  },
  {
    id: "agentCoral",
    name: "Agent Coral",
    description: "Expressive and collaborative with warm coral surfaces, bold orange energy, and charcoal structure.",
    previewColors: {
      primary: "#ff5a36",
      secondary: "#ffad97",
      bg: "#f7f4ef",
      foreground: "#fffdfb",
      textPrimary: "#2f2c2a",
    },
  },
];

type RgbColor = [number, number, number];

const BASE_THEME_COLORS: Required<CustomThemeColors> = {
  primary: "#422AFB",
  secondary: "#7551FF",
  bg: "#F4F7FE",
  foreground: "#ffffff",
  textPrimary: "#1b2559",
};

const clamp = (value: number) => Math.max(0, Math.min(255, Math.round(value)));

const hexToRgb = (hex: string): RgbColor => {
  const normalized = hex.replace("#", "");
  const value = normalized.length === 3
    ? normalized.split("").map((char) => char + char).join("")
    : normalized;
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return hexToRgb("#000000");
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
};

const rgbToHex = ([red, green, blue]: RgbColor) =>
  `#${[red, green, blue].map((channel) => clamp(channel).toString(16).padStart(2, "0")).join("")}`;

const mix = (first: string, second: string, amount: number) => {
  const firstRgb = hexToRgb(first);
  const secondRgb = hexToRgb(second);
  return rgbToHex([
    firstRgb[0] + (secondRgb[0] - firstRgb[0]) * amount,
    firstRgb[1] + (secondRgb[1] - firstRgb[1]) * amount,
    firstRgb[2] + (secondRgb[2] - firstRgb[2]) * amount,
  ]);
};

const relativeLuminance = (color: string) => {
  const channels = hexToRgb(color).map((channel) => channel / 255);
  const linearChannels = channels.map((channel) =>
    channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * linearChannels[0] + 0.7152 * linearChannels[1] + 0.0722 * linearChannels[2];
};

const contrastRatio = (first: string, second: string) => {
  const firstLuminance = relativeLuminance(first);
  const secondLuminance = relativeLuminance(second);
  const lighter = Math.max(firstLuminance, secondLuminance);
  const darker = Math.min(firstLuminance, secondLuminance);
  return (lighter + 0.05) / (darker + 0.05);
};

const readableText = (background: string, preferred: string) =>
  contrastRatio(background, preferred) >= 4.5
    ? preferred
    : contrastRatio(background, "#ffffff") >= contrastRatio(background, "#000000")
      ? "#ffffff"
      : "#000000";

const normalizeBaseColors = (colors?: Record<string, string> | CustomThemeColors) =>
  (Object.keys(BASE_THEME_COLORS) as Array<keyof CustomThemeColors>).reduce((result, key) => {
    const value = colors?.[key];
    result[key] = typeof value === "string" && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value)
      ? value
      : BASE_THEME_COLORS[key];
    return result;
  }, {} as Required<CustomThemeColors>);

/** Builds the complete palette from five user-controlled base colors. */
export const buildCustomCssVars = (
  customTheme?: Record<string, string> | CustomThemeColors,
  mode: ThemeMode = "light"
): Record<string, string> => {
  const colors = normalizeBaseColors(customTheme);
  const lightText = readableText(colors.bg, colors.textPrimary);
  const secondarySurface = mix(colors.bg, colors.foreground, 0.5);
  const mutedText = mix(colors.textPrimary, colors.bg, 0.45);
  const border = mix(colors.textPrimary, colors.foreground, 0.82);
  const brandScale = {
    50: mix(colors.bg, colors.primary, 0.08),
    100: mix(colors.bg, colors.primary, 0.18),
    200: mix(colors.bg, colors.primary, 0.32),
    300: mix(colors.bg, colors.primary, 0.5),
    400: colors.secondary,
    500: colors.primary,
    700: mix(colors.primary, colors.textPrimary, 0.4),
    900: mix(colors.primary, colors.textPrimary, 0.78),
  };
  const navyScale = {
    400: mix(colors.foreground, colors.textPrimary, 0.6),
    500: mix(colors.foreground, colors.textPrimary, 0.72),
    600: mix(colors.foreground, colors.textPrimary, 0.82),
    700: mix(colors.foreground, colors.textPrimary, 0.88),
    800: mix(colors.foreground, colors.textPrimary, 0.93),
    900: colors.textPrimary,
  };

  const palette = {
    "--color-light-primary": colors.bg,
    "--color-blue-secondary": colors.secondary,
    "--color-brand-linear": mix(colors.primary, colors.secondary, 0.5),
    ...Object.fromEntries(Object.entries(brandScale).map(([step, value]) => [`--color-brand-${step}`, value])),
    ...Object.fromEntries(Object.entries(navyScale).map(([step, value]) => [`--color-navy-${step}`, value])),
    "--color-copy-btn-surface": colors.secondary,
    "--color-copy-btn-icon": readableText(colors.secondary, lightText),
    "--color-download-btn-surface": colors.primary,
    "--color-download-btn-icon": readableText(colors.primary, lightText),
    "--color-text-primary": colors.textPrimary,
    "--color-text-secondary": mutedText,
    "--color-text-muted": mix(mutedText, colors.bg, 0.4),
    "--color-surface-primary": colors.foreground,
    "--color-surface-secondary": secondarySurface,
    "--color-border-primary": border,
    "--color-shadow-primary": colors.textPrimary,
    "--color-shadow-secondary": colors.textPrimary,
    "--color-scrollbar-thumb": border,
    "--color-scrollbar-track": colors.bg,
  };

  if (mode === "dark") {
    return Object.fromEntries(Object.entries(palette).map(([key, value]) =>
      key.includes("brand-400") || key.includes("brand-500") || key.includes("blue-secondary") || key.includes("brand-linear") || key.includes("btn-surface")
        ? [key, darkButtonColor(value)]
        : [key, value]
    ));
  }

  return palette;
};

/**
 * Converts a CustomThemeColors object into CSS variable overrides.
 */
/**
 * Retrieves the default preview / editable colors for a given theme ID.
 */
export const getDefaultColorsForTheme = (themeId: string, mode: ThemeMode = "light"): CustomThemeColors => {
  const match = THEME_OPTIONS.find((t) => t.id === themeId);
  if (match) {
    const palette = THEME_PRESETS[themeId]?.[mode] || THEME_PRESETS.horizonDefault[mode];
    return {
      primary: palette["--color-brand-500"],
      secondary: palette["--color-brand-400"],
      bg: palette["--color-light-primary"],
      foreground: palette["--color-surface-primary"],
      textPrimary: palette["--color-text-primary"],
    };
  }
  return { ...THEME_OPTIONS[0].previewColors };
};

/**
 * Applies a theme (preset CSS variables + optional custom overrides) to the DOM root.
 */
export const applyTheme = (
  themeName: string,
  customColors?: Record<string, string>,
  mode: ThemeMode = "light"
) => {
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith("/auth")) return;

  const preset = THEME_PRESETS[themeName]?.[mode] || THEME_PRESETS.horizonDefault[mode];

  if (preset) {
    for (const [key, val] of Object.entries(preset)) {
      if (key !== "name") {
        document.documentElement.style.setProperty(key, val as string);
      }
    }

    if (customColors) {
      for (const [key, val] of Object.entries(customColors)) {
        document.documentElement.style.setProperty(key, val);
      }
    }

    if (mode === "dark") {
      document.documentElement.classList.add("dark");
      document.body.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
      document.body.classList.remove("dark");
    }
  }
};
